"use client";

import * as React from "react";
import { ArrowRight } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Link } from "@/components/common/Link";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  per_page: 6,
  product_cat: [17]
};
const wp_query = new WP_Query(wp_query_params);
const filterOptions = [{
  value: "garbage-bags",
  label: "Garbage Bags"
}];
function formatPrice(price: number) {
  return `USD ${Math.round(price).toLocaleString("en-US")}`;
}
function MasterclassesGrid() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-12 lg:gap-y-20">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="space-y-4">
            <div className="aspect-[4/5] w-full bg-muted/20 animate-pulse rounded-md" />
            <div className="h-3 w-1/2 bg-muted/20 animate-pulse rounded-md" />
            <div className="h-6 w-3/4 bg-muted/20 animate-pulse rounded-md" />
            <div className="h-4 w-1/2 bg-muted/20 animate-pulse rounded-md" />
          </div>)}
      </div>;
  }
  if (error) {
    return <p className="text-sm text-muted-foreground font-mono">
        Unable to load products at this moment.
      </p>;
  }
  if (products.length === 0) {
    return <p className="text-sm text-muted-foreground font-mono">
        No wholesale products available.
      </p>;
  }
  return <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-12 lg:gap-y-20">
      {products.map((product, i) => {
      return <article data-index={i} key={product.id} className="group flex flex-col">
            <Link to={product.link} className="block">
              <div className="relative overflow-hidden">
                <AspectRatio ratio={4 / 5}>
                  {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:translate-x-1" /> : <div className="h-full w-full bg-muted/30" />}
                  <div className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-overlay" style={{
                backgroundImage: "radial-gradient(var(--foreground) 1px, transparent 1px)",
                backgroundSize: "3px 3px"
              }} />
                  <div className="absolute top-4 right-4 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                    <Badge variant="default" className="rounded-md border-transparent px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest" style={{
                  backgroundColor: "hsl(25 95% 50%)",
                  color: "var(--primary-foreground)"
                }}>
                      + View Specs
                    </Badge>
                  </div>
                </AspectRatio>
              </div>
            </Link>

            <Link to={product.link} className="mt-6 block">
              <h3 className="font-default text-2xl font-normal leading-snug text-foreground transition-colors hover:text-primary">
                {product.title}
              </h3>
            </Link>

            <div className="mt-3 flex flex-wrap gap-2">
              <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-wider border-muted-foreground/30 text-muted-foreground">
                Custom Sizes
              </Badge>
              <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-wider border-muted-foreground/30 text-muted-foreground">
                High-Density PE
              </Badge>
              <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-wider border-muted-foreground/30 text-muted-foreground">
                MOQ: 500kg
              </Badge>
            </div>

            <div className="mt-auto pt-6">
              <Separator className="mb-4 bg-border/60" />
              <div className="flex items-center justify-between gap-4">
                <span className="font-default text-2xl font-light text-foreground">
                  {formatPrice(product.price)}
                </span>
                <AddToCartButton productId={product.id} variant="link" text="Request Quote" showQuantity={false} className="w-auto" buttonClassName="bg-primary text-primary-foreground font-mono text-xs uppercase tracking-widest px-0 hover:underline underline-offset-4" />
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function FeaturedWholesaleProducts() {
  const [filter, setFilter] = React.useState("garbage-bags");
  return <section data-section-id="4694" id="featured-wholesale-products" className="bg-background text-foreground py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-20">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between lg:gap-16">
          <div className="max-w-2xl">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground mb-6">
              03 — Industrial Solutions
            </p>
            <h2 className="font-default text-4xl font-light leading-[1.1] md:text-5xl lg:text-6xl text-foreground">
              Engineered for
              <br />
              <em className="italic font-light">industrial scale.</em>
            </h2>
          </div>

          <div className="flex-shrink-0">
            <ToggleGroup type="single" value={filter} onValueChange={v => v && setFilter(v)} className="flex flex-wrap gap-1 rounded-md" variant="default">
              {filterOptions.map((opt, i) => <ToggleGroupItem data-index={i} key={opt.value} value={opt.value} className="rounded-md border-0 bg-transparent px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground transition-colors hover:bg-transparent hover:text-foreground data-[state=on]:bg-transparent data-[state=on]:text-foreground data-[state=on]:border-b-2 data-[state=on]:border-primary data-[state=on]:rounded-none">
                  {opt.label}
                </ToggleGroupItem>)}
            </ToggleGroup>
          </div>
        </div>

        <Separator className="mt-12 mb-16 lg:mt-16 lg:mb-20 bg-border/40" />

        <EcommerceProductsProvider wp_query={{
        per_page: 6,
        product_cat: [17]
      }}>
          <MasterclassesGrid />
        </EcommerceProductsProvider>

        <div className="mt-20 flex justify-center lg:mt-24">
          <Link to="#">
            <Button variant="outline" size="lg" className="rounded-md border-border bg-transparent px-8 py-6 font-mono text-xs uppercase tracking-[0.2em] text-foreground hover:bg-primary hover:text-primary-foreground">
              View All Products
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}